import axiosClient from "utils/axios";
import { ApiOptionList, ApiResponse } from "../../../types/api.types";
import {
  CreateMaterialPayload,
  Material,
  MaterialsTableResponse,
  UpdateMaterialPayload,
} from "../types/material.types";

function normalizeListResponse(
  raw: Material[] | MaterialsTableResponse,
  page: number,
  limit: number,
  search?: string,
  isActive?: boolean,
): MaterialsTableResponse {
  let rows = Array.isArray(raw) ? raw : raw.rows;

  if (search?.trim()) {
    const q = search.trim().toLowerCase();
    rows = rows.filter((r) => r.name?.toLowerCase().includes(q));
  }

  if (isActive !== undefined) {
    rows = rows.filter((r) => r.isActive === isActive);
  }

  const totalItems = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  const start = (page - 1) * limit;
  const pageRows = rows.slice(start, start + limit);

  return {
    rows: pageRows,
    totalItems,
    currentPage: page,
    pageSize: limit,
    limit,
    totalPages,
  };
}

export const getMaterials = async ({
  page = 1,
  limit = 10,
  search,
  isActive,
}: {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
} = {}) => {
  try {
    const response = await axiosClient.get<
      ApiResponse<MaterialsTableResponse | { rows: Material[] }>
    >("/materials", {
      params: {
        query: search || undefined,
        page,
        limit,
        isActive,
      },
    });

    const payload = response.data.data;

    if (!payload) {
      const normalized = normalizeListResponse(
        [],
        page,
        limit,
        search,
        isActive,
      );
      return {
        success: response.data.success,
        message: response.data.message,
        data: normalized,
      } satisfies ApiResponse<MaterialsTableResponse>;
    }

    if (
      payload &&
      typeof payload === "object" &&
      "totalItems" in payload &&
      Array.isArray(payload.rows)
    ) {
      return response.data as ApiResponse<MaterialsTableResponse>;
    }

    const rows = (payload as { rows?: Material[] })?.rows ?? [];
    const normalized = normalizeListResponse(
      rows,
      page,
      limit,
      search,
      isActive,
    );

    return {
      success: response.data.success,
      message: response.data.message,
      data: normalized,
    } satisfies ApiResponse<MaterialsTableResponse>;
  } catch (err) {
    console.warn("Error fetching materials: ", err);
    throw err;
  }
};

export const getMaterialOptions = async ({
  query,
  limit = 20,
  page = 1,
}: {
  query?: string;
  limit?: number;
  page?: number;
}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      "/materials/options",
      {
        params: {
          query,
          limit,
          page,
        },
      },
    );

    return response.data;
  } catch (err) {
    console.warn("Error fetching material options: ", err);
    throw err;
  }
};

export const getMaterial = async (id: number) => {
  try {
    const response = await axiosClient.get<ApiResponse<Material>>(
      `/materials/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching material: ", err);
    throw err;
  }
};

export const createMaterial = async (payload: CreateMaterialPayload) => {
  try {
    const response = await axiosClient.post<ApiResponse<Material>>(
      "/materials",
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating material: ", err);
    throw err;
  }
};

export const updateMaterial = async (
  id: number,
  payload: UpdateMaterialPayload,
) => {
  try {
    const response = await axiosClient.put<ApiResponse<Material>>(
      `/materials/${id}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating material: ", err);
    throw err;
  }
};

export const deleteMaterial = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<Material>>(
      `/materials/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting material: ", err);
    throw err;
  }
};
